import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// Layout Components (Keep static for immediate visibility)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Lazy Load Pages for Code Splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const TeslaRepairPage = lazy(() => import('./pages/TeslaRepairPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

const pageTitles = {
  '/': 'Douglaston Auto Care | Expert Vehicle Service',
  '/services': 'Auto Repair Services | Douglaston Auto Care',
  '/tesla-repair': 'Tesla Service | Douglaston Auto Care',
  '/about': 'About Douglaston Auto Care | Queens Shop',
  '/contact': 'Contact Douglaston Auto Care | Queens NY',
  '/gallery': 'Auto Care Gallery | Douglaston Auto Care',
  '/privacy-policy': 'Privacy Policy | Douglaston Auto Care',
  '/terms-of-service': 'Terms of Service | Douglaston Auto Care',
  '/accessibility': 'Accessibility | Douglaston Auto Care',
  '/sitemap': 'Sitemap | Douglaston Auto Care'
};

// Loading Fallback Component
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-white">
    <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Page transition wrapper component
const PageWrapper = ({ children }) => {
  const location = useLocation();
  
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        duration: 0.3, 
        ease: "easeOut"
      }}
    >
      {children}
    </motion.div>
  );
};

const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = pageTitles[location.pathname] || 'Douglaston Auto Care | Expert Vehicle Service';
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ScrollToTop />
      
      <main id="main-content" role="main">
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={
                <PageWrapper>
                  <HomePage />
                </PageWrapper>
              } />
              <Route path="/services" element={
                <PageWrapper>
                  <ServicesPage />
                </PageWrapper>
              } />
              <Route path="/tesla-repair" element={
                <PageWrapper>
                  <TeslaRepairPage />
                </PageWrapper>
              } />
              <Route path="/about" element={
                <PageWrapper>
                  <AboutPage />
                </PageWrapper>
              } />
              <Route path="/contact" element={
                <PageWrapper>
                  <ContactPage />
                </PageWrapper>
              } />
              <Route path="/gallery" element={
                <PageWrapper>
                  <GalleryPage />
                </PageWrapper>
              } />
              <Route path="/privacy-policy" element={
                <PageWrapper>
                  <PrivacyPolicyPage />
                </PageWrapper>
              } />
              <Route path="/terms-of-service" element={
                <PageWrapper>
                  <TermsOfServicePage />
                </PageWrapper>
              } />
              <Route path="/accessibility" element={
                <PageWrapper>
                  <AccessibilityPage />
                </PageWrapper>
              } />
              <Route path="/sitemap" element={
                <PageWrapper>
                  <SitemapPage />
                </PageWrapper>
              } />
              <Route path="*" element={
                <PageWrapper>
                  <NotFound />
                </PageWrapper>
              } />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>
      
      <Footer />
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;